import { useHover } from "@redotech/react-util/hover";
import DotsReorderIcon from "@redotech/redo-icons/dots-reorder.svg";
import classNames from "classnames";
import { memo, useState } from "react";
import { useMatch } from "react-router-dom";
import { Flex } from "../../../../flex";
import { Text } from "../../../../text";
import { TextWeightValue } from "../../../../theme/typography";
import { OverflowTooltip } from "../../../../tooltip/overflow-tooltip";
import { RedoBadge } from "../../../badge/redo-badge";
import { RedoIcon } from "../../../icon/redo-icon";
import * as navigationCss from "../../redo-navigation.module.css";
import { formatNotificationCount } from "../../secondary/format-notification-count";
import { secondaryNavTabTestId } from "../secondary-nav-tab-test-id";
import type { TreeIconComponent } from "../types";

export interface RedoViewItemProps {
  name: string;
  "data-dd-action-name"?: string;
  friendlyName: string;
  /** URL for link mode. Omit when using `onClick` for state-driven selection. */
  linkHref?: string;
  dragHandleProps: object;
  clone?: boolean;
  notificationCount?: number;
  /**
   * When true, the notification badge will always use the black/outline style
   * regardless of the count (no red "attention" indicator).
   */
  muted?: boolean;
  /** Escape hatch for state-driven selection; overrides URL match when provided. */
  selected?: boolean;
  /** When provided, the row renders as a button and fires this handler on click. */
  onClick?: () => void;
  /**
   * Fires on every row click in addition to navigation/select behavior. Use for
   * analytics or other side effects that should not replace the primary action.
   */
  onItemClick?: () => void;
  /** Optional leading icon. */
  Icon?: TreeIconComponent;
  /** When true, hides the drag-handle affordance. */
  disableDrag?: boolean;
  /** When true, the row renders at reduced opacity to signal an inactive state. */
  dimmed?: boolean;
}

export const RedoViewItem = memo(function RedoViewItem({
  name,
  friendlyName,
  dragHandleProps,
  linkHref,
  clone,
  notificationCount,
  muted,
  selected,
  onClick,
  onItemClick,
  Icon,
  disableDrag,
  dimmed,
  "data-dd-action-name": actionName,
}: RedoViewItemProps) {
  const matched = useMatch({
    path: linkHref ? `${linkHref}/*` : "/__redo_never_match__",
    caseSensitive: true,
  });
  const active = selected ?? !!matched;

  const color: "primary" | "secondary" = active ? "primary" : "secondary";
  const textWeight: TextWeightValue = active ? "semibold" : "regular";

  const [overflowRef, setOverflowRef] = useState<HTMLDivElement | null>(null);
  const [componentRef, setComponentRef] = useState<HTMLDivElement | null>(null);

  const hovered = useHover(componentRef);

  const asButton = onClick !== undefined || linkHref === undefined;
  const sharedProps = {
    "data-dd-action-name": actionName,
    align: "center" as const,
    className: classNames(
      navigationCss.navItem,
      active && navigationCss.active,
    ),
    "data-testid": secondaryNavTabTestId(name),
    gap: "md" as const,
    justify: "space-between" as const,
    px: "sm" as const,
    py: "sm" as const,
    borderRadius: "md" as const,
    ref: setComponentRef,
    style: dimmed ? { opacity: 0.55 } : undefined,
    w: "full" as const,
    ...dragHandleProps,
  };

  const body = (
    <>
      {Icon ? (
        <Flex align="center" shrink={0}>
          <RedoIcon color="tertiary" Icon={Icon} size={16} />
        </Flex>
      ) : null}
      <OverflowTooltip
        hideOnHover
        overflowRef={overflowRef}
        tooltipProps={{ title: friendlyName, darkMode: true }}
        wrapperStyle={{
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          flex: 1,
          minWidth: 0,
        }}
      >
        <Text
          fontSize="xs"
          fontWeight={textWeight}
          overflow="hidden"
          ref={setOverflowRef}
          textColor={color}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {friendlyName}
        </Text>
      </OverflowTooltip>
      {!disableDrag && (
        <Flex
          align="center"
          className={classNames(
            hovered && navigationCss.show,
            navigationCss.dragHandle,
            clone && navigationCss.clone,
          )}
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <RedoIcon color="tertiary" Icon={DotsReorderIcon} size={16} />
        </Flex>
      )}
      {notificationCount !== undefined && (
        <Flex>
          <RedoBadge
            className={navigationCss.notificationCountBadge}
            color={muted || notificationCount === 0 ? "inactive" : "error"}
            darkMode
            size="xs"
            text={formatNotificationCount(notificationCount)}
            type="primary"
          />
        </Flex>
      )}
    </>
  );

  if (asButton) {
    return (
      <Flex
        as="button"
        data-arbiter="RedoViewItem"
        onClick={(e) => {
          e.preventDefault();
          onItemClick?.();
          onClick?.();
        }}
        {...sharedProps}
      >
        {body}
      </Flex>
    );
  }

  return (
    <Flex
      as="a"
      data-arbiter="RedoViewItem"
      linkProps={{ to: linkHref! }} // eslint-disable-line @typescript-eslint/no-non-null-assertion
      onClick={onItemClick}
      {...sharedProps}
    >
      {body}
    </Flex>
  );
});
